import { useEffect, useId, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import type { Facet, Kolom } from '@xxtract/tabelfilters';
import { useKlantenlijst } from '@/features/processes/hooks/useKlantenlijst';
import { CONTROL_CLASS, MELDING_CLASS, POPUP_CLASS, regelClass } from './stijl';

const OPEN_ICON = 'expand_more';
const CHECK_ICON = 'check';
/** Vanaf zoveel keuzes staat er een zoekveld boven de lijst (§3). */
const ZOEKVELD_VANAF = 9;

interface KeuzelijstFilterProps {
  /** Alleen de beschrijving van de kolom; `never` laat elke rijvorm toe. */
  kolom: Kolom<never, never>;
  keuzes: readonly Facet[];
  gekozen: readonly (string | null)[];
  onChange: (gekozen: (string | null)[]) => void;
  /** Extern gepagineerd: de bron telt niet (CAP-10). */
  zonderAantallen?: boolean;
  /** De set is bij de bron afgekapt (CAP-9). */
  afgekapt?: boolean;
  /** Id van een uitlegregel buiten de knop (Processen-status, story 12). */
  beschrijvingId?: string;
}

/** Meerkeuzefilter met aantallen, gekozen waarden bovenaan en `(leeg)` voor lege waarden (CAP-2, §3). */
export function KeuzelijstFilter({ kolom, keuzes, gekozen, onChange, zonderAantallen, afgekapt, beschrijvingId }: KeuzelijstFilterProps) {
  const { t } = useTranslation();
  const id = useId();
  const [open, setOpen] = useState(false);
  // De actieve regel volgt zijn waarde, niet zijn plek: na aanvinken schuift hij naar boven (gekozen eerst).
  const [actiefWaarde, setActiefWaarde] = useState<{ waarde: string | null } | null>(null);
  const [zoek, setZoek] = useState('');
  const knop = useRef<HTMLButtonElement>(null);
  const lijstRef = useRef<HTMLUListElement>(null);
  const zoekRef = useRef<HTMLInputElement>(null);

  const vanKlanten = kolom.bron === 'klantenlijst';
  const klantenlijst = useKlantenlijst(vanKlanten || Boolean(kolom.naamOpzoeker));
  const klanten = klantenlijst?.data;
  const kop = t(kolom.kopSleutel);

  const label = (waarde: string | null): string => {
    if (waarde === null) return t('tabel.leeg');
    if (vanKlanten || kolom.naamOpzoeker) return klanten?.find((k) => k.gln === waarde)?.companyName ?? waarde;
    return kolom.waardeLabel ? kolom.waardeLabel(waarde, t) : waarde;
  };

  const regels = useMemo<Facet[]>(() => {
    if (!vanKlanten || !klanten) return [...keuzes];
    // Klantkolom: de volledige klantenlijst, nooit alleen de klanten van de geladen pagina (CAP-10).
    const aantal = new Map(keuzes.map((k) => [k.waarde, k.aantal]));
    const kies = new Set(gekozen);
    const naam = new Map(klanten.map((k) => [k.gln, k.companyName]));
    const uit: Facet[] = klanten.map((k) => ({ waarde: k.gln, aantal: aantal.get(k.gln) ?? 0 }));
    for (const w of gekozen) if (w !== null && !naam.has(w)) uit.push({ waarde: w, aantal: 0 });
    const tekst = (w: string | null) => (w === null ? '' : (naam.get(w) ?? w));
    return uit.sort((a, b) => Number(kies.has(b.waarde)) - Number(kies.has(a.waarde)) || tekst(a.waarde).localeCompare(tekst(b.waarde), 'nl'));
  }, [vanKlanten, klanten, keuzes, gekozen]);

  const metZoekveld = regels.length >= ZOEKVELD_VANAF;
  const term = zoek.trim().toLocaleLowerCase('nl');
  const zichtbaar = metZoekveld && term
    ? regels.filter((r) => label(r.waarde).toLocaleLowerCase('nl').includes(term) || (r.waarde ?? '').toLocaleLowerCase('nl').includes(term))
    : regels;

  const actief = actiefWaarde ? zichtbaar.findIndex((r) => r.waarde === actiefWaarde.waarde) : -1;
  const setActief = (i: number) => setActiefWaarde(zichtbaar[i] ? { waarde: zichtbaar[i]!.waarde } : null);

  useEffect(() => {
    if (open) (zoekRef.current ?? lijstRef.current)?.focus();
  }, [open]);

  const sluit = (focusTerug: boolean) => {
    setOpen(false);
    setActief(-1);
    setZoek('');
    if (focusTerug) knop.current?.focus();
  };

  const wissel = (waarde: string | null) => {
    const staatAan = gekozen.includes(waarde);
    if (kolom.enkelvoudig) onChange(staatAan ? [] : [waarde]);
    else onChange(staatAan ? gekozen.filter((w) => w !== waarde) : [...gekozen, waarde]);
  };

  const toets = (e: React.KeyboardEvent) => {
    if (e.key === 'Escape') {
      e.preventDefault();
      sluit(true);
    } else if (e.key === 'ArrowDown') {
      e.preventDefault();
      lijstRef.current?.focus();
      setActief(Math.min(actief + 1, zichtbaar.length - 1));
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setActief(Math.max(actief - 1, 0));
    } else if ((e.key === ' ' || e.key === 'Enter') && e.currentTarget === lijstRef.current && zichtbaar[actief]) {
      e.preventDefault();
      wissel(zichtbaar[actief]!.waarde);
    }
  };

  const lijstId = `${id}-lijst`;
  return (
    <div
      className="relative w-full md:w-auto"
      onBlur={(e) => {
        if (open && !e.currentTarget.contains(e.relatedTarget as Node | null)) sluit(false);
      }}
    >
      <button
        aria-controls={open ? lijstId : undefined}
        aria-describedby={beschrijvingId}
        aria-expanded={open}
        aria-haspopup="listbox"
        className={`${CONTROL_CLASS} inline-flex w-full items-center justify-between gap-1 md:w-auto`}
        onClick={() => (open ? sluit(false) : setOpen(true))}
        ref={knop}
        type="button"
      >
        <span>
          {kop}
          {gekozen.length > 0 ? ` (${gekozen.length})` : ''}
        </span>
        <span aria-hidden="true" className="material-symbols-outlined text-[18px] text-[var(--foreground-muted)]">{OPEN_ICON}</span>
      </button>

      {open ? (
        <div className={POPUP_CLASS}>
          {metZoekveld ? (
            <div className="px-2 pb-1">
              <input
                aria-label={t('tabel.zoekInKeuzes')}
                className={`${CONTROL_CLASS} w-full`}
                onChange={(e) => {
                  setZoek(e.target.value);
                  setActief(-1);
                }}
                onKeyDown={toets}
                placeholder={t('tabel.zoekInKeuzes')}
                ref={zoekRef}
                type="search"
                value={zoek}
              />
            </div>
          ) : null}
          {zonderAantallen ? <p className={MELDING_CLASS}>{t('tabel.geenAantallenBijBron')}</p> : null}
          <ul
            aria-activedescendant={actief >= 0 && zichtbaar[actief] ? `${id}-${actief}` : undefined}
            aria-label={kop}
            aria-multiselectable={!kolom.enkelvoudig}
            className="max-h-64 overflow-y-auto outline-none"
            id={lijstId}
            onKeyDown={toets}
            ref={lijstRef}
            role="listbox"
            tabIndex={-1}
          >
            {zichtbaar.map((r, i) => {
              const aan = gekozen.includes(r.waarde);
              return (
                <li
                  aria-selected={aan}
                  className={regelClass(i === actief)}
                  id={`${id}-${i}`}
                  key={r.waarde ?? '\u0000'}
                  onClick={() => wissel(r.waarde)}
                  onMouseDown={(e) => e.preventDefault()}
                  ref={i === actief ? (el) => el?.scrollIntoView?.({ block: 'nearest' }) : undefined}
                  role="option"
                >
                  <span
                    aria-hidden="true"
                    className={`material-symbols-outlined inline-flex h-4 w-4 shrink-0 items-center justify-center rounded-sm border text-[14px] ${aan ? 'border-[var(--secondary)] bg-[var(--secondary)] text-[var(--background)]' : 'border-[var(--border)] text-transparent'}`}
                  >
                    {CHECK_ICON}
                  </span>
                  <span className="flex-1 truncate">{label(r.waarde)}</span>
                  {zonderAantallen ? null : <span className="text-xs tabular-nums text-[var(--foreground-muted)]">{r.aantal}</span>}
                </li>
              );
            })}
          </ul>
          {afgekapt ? <p className={`${MELDING_CLASS} border-t border-[var(--border)]`}>{t('tabel.aantallenOnvolledig')}</p> : null}
        </div>
      ) : null}
    </div>
  );
}
